import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** normalize like in parent */
const normalizeCategories = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  return list.map((c) => ({
    ...c,
    is_filled: Boolean(Number(c?.pivot?.is_filled ?? c?.is_filled ?? 0)),
  }));
};

const byCatName = (c, locale) => c?.[`name_${locale}`] || c?.name || '';

export default function GroupCategoriesInlineEditor({
  group,
  locale = 'kz',
  t,
  axios,
  catRefs = [],
  disabled = false,
  isPreSchool = false,
  onNeedFillCategory,
  onChanged,
  hideToolbar = false,
  editSignal = 0,      // внешний триггер (пинок)
  isEditing,           // управляемый режим редактирования (true/false)
}) {
  const [editMode, setEditMode] = useState(false);
  const [busyIds, setBusyIds] = useState({});
  const [showAdder, setShowAdder] = useState(false);
  const [q, setQ] = useState('');
  const [savingAdd, setSavingAdd] = useState(false);

  // popup refs / positioning
  const toolbarAddBtnRef = useRef(null);
  const chipAddBtnRef = useRef(null);
  const adderRef = useRef(null);
  const searchInputRef = useRef(null);

  const [adderPos, setAdderPos] = useState({
    top: 0,
    left: 0,
    width: 420,
    placement: 'bottom', // 'bottom' | 'top'
  });

  const cats = useMemo(() => normalizeCategories(group?.categories || []), [group?.categories]);

  const existingIds = useMemo(
    () => new Set(cats.map((c) => String(c.id))),
    [cats]
  );

  const canEdit = !disabled && String(group?.id) !== 'no_group';
  const canAdd = canEdit && !isPreSchool;

  // Управляемый режим из родителя (toggle)
  useEffect(() => {
    if (typeof isEditing !== 'boolean') return;

    if (!canEdit) {
      setEditMode(false);
      setShowAdder(false);
      setQ('');
      return;
    }

    setEditMode(isEditing);

    // если выключаем редактирование — закрываем панель добавления и очищаем поиск
    if (!isEditing) {
      setShowAdder(false);
      setQ('');
    }
  }, [isEditing, canEdit]);

  // Фолбэк: если родитель не передал isEditing, тогда старый режим через сигнал
  useEffect(() => {
    if (typeof isEditing === 'boolean') return; // управление идет из родителя
    if (!editSignal) return;
    if (!canEdit) return;

    setEditMode(true);
  }, [editSignal, canEdit, isEditing]);

  const filteredToAdd = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (catRefs || [])
      .filter((c) => !existingIds.has(String(c.id)))
      .filter((c) => {
        const name = String(byCatName(c, locale)).toLowerCase();
        return !query || name.includes(query);
      });
  }, [catRefs, existingIds, q, locale]);

  const setBusy = (id, v) => setBusyIds((prev) => ({ ...prev, [id]: v }));

  const handleChipClick = (cat) => {
    // если редактируем — обычный клик по чипу не должен открывать форму
    if (editMode) return;
    if (cat?.is_filled) return; // заполненные не кликаем
    onNeedFillCategory?.(cat.id);
  };

  const closeAdder = () => {
    setShowAdder(false);
    setQ('');
  };

const computeAndSetAdderPosition = (anchorEl) => {
  if (!anchorEl) return;

  const r = anchorEl.getBoundingClientRect();
  const popupWidth = Math.min(420, Math.max(320, Math.floor(window.innerWidth - 16)));
  const estimatedHeight = 360;
  const gap = 8;

  let left = r.left;
  if (left + popupWidth > window.innerWidth - 8) {
    left = window.innerWidth - popupWidth - 8;
  }
  if (left < 8) left = 8;

  const spaceAbove = r.top;
  const spaceBelow = window.innerHeight - r.bottom;

  // 👇 Предпочитаем вверх
  let openUp = true;

  // fallback вниз, если сверху мало места, а снизу больше
  if (spaceAbove < 180 && spaceBelow > spaceAbove) {
    openUp = false;
  }

  let top = openUp ? (r.top - estimatedHeight - gap) : (r.bottom + gap);

  if (top < 8) top = 8;
  if (top + estimatedHeight > window.innerHeight - 8) {
    top = Math.max(8, window.innerHeight - estimatedHeight - 8);
  }

  setAdderPos({
    top,
    left,
    width: popupWidth,
    placement: openUp ? 'top' : 'bottom',
  });
};

  const toggleAdder = (anchor) => {
    if (!canAdd || !editMode) return;

    const anchorEl =
      anchor === 'toolbar'
        ? toolbarAddBtnRef.current
        : chipAddBtnRef.current || toolbarAddBtnRef.current;

    setShowAdder((prev) => {
      const next = !prev;
      if (next && anchorEl) {
        computeAndSetAdderPosition(anchorEl);
      }
      if (!next) {
        setQ('');
      }
      return next;
    });
  };

  const removeCategory = async (cat) => {
    // удалять только незаполненную
    if (!canEdit || cat?.is_filled) return;

    const ok = window.confirm(
      locale === 'ru'
        ? `Удалить категорию «${byCatName(cat, locale)}» из группы?`
        : `«${byCatName(cat, locale)}» санатын топтан өшіру керек пе?`
    );
    if (!ok) return;

    try {
      setBusy(cat.id, true);
      await axios.post(
        `/publisher/groups/${group.id}/categories/remove`,
        { category_ids: [Number(cat.id)] },
        {
          headers: {
            'Accept-Language': locale,
            'Content-Type': 'application/json',
          },
        }
      );
      await onChanged?.();
    } catch (e) {
      console.error('remove category error', e);
      alert(t?.addCatsErr || (locale === 'ru' ? 'Ошибка' : 'Қате'));
    } finally {
      setBusy(cat.id, false);
    }
  };

  const addCategory = async (catId) => {
    if (!canAdd) return;
    try {
      setSavingAdd(true);
      await axios.post(
        `/publisher/groups/${group.id}/categories/add`,
        { category_ids: [Number(catId)] },
        {
          headers: {
            'Accept-Language': locale,
            'Content-Type': 'application/json',
          },
        }
      );
      await onChanged?.();
      setQ('');
    } catch (e) {
      console.error('add category error', e);
      alert(t?.addCatsErr || (locale === 'ru' ? 'Ошибка' : 'Қате'));
    } finally {
      setSavingAdd(false);
    }
  };

  // Закрытие popup по клику вне / Esc / resize / scroll
  useEffect(() => {
    if (!showAdder) return;

    const onPointerDown = (e) => {
      const target = e.target;

      const inPopup = adderRef.current?.contains(target);
      const inToolbarBtn = toolbarAddBtnRef.current?.contains(target);
      const inChipBtn = chipAddBtnRef.current?.contains(target);

      if (inPopup || inToolbarBtn || inChipBtn) return;
      closeAdder();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeAdder();
      }
    };

    const onReposition = () => {
      const anchorEl = toolbarAddBtnRef.current || chipAddBtnRef.current;
      if (anchorEl) computeAndSetAdderPosition(anchorEl);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReposition);
    // capture=true чтобы ловить скролл любых родительских контейнеров
    window.addEventListener('scroll', onReposition, true);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [showAdder]);

  // фокус в поиск после открытия
  useEffect(() => {
    if (!showAdder) return;
    const id = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(id);
  }, [showAdder]);

  // если отключили редактирование / нельзя редактировать — закрыть popup
  useEffect(() => {
    if (!editMode || !canAdd) {
      setShowAdder(false);
      setQ('');
    }
  }, [editMode, canAdd]);

  // popup via portal (поверх всего, не двигает контент)
  const adderPopup = (editMode && showAdder && canAdd)
    ? createPortal(
        <div
          ref={adderRef}
          className="gcie__adder gcie__adder--popup"
          style={{
            position: 'fixed',
            top: `${adderPos.top}px`,
            left: `${adderPos.left}px`,
            width: `${adderPos.width}px`,
            zIndex: 9999,
            maxHeight: '400px',
            overflow: 'hidden',
            background: '#fff',
            border: '1px solid #d9e0ea',
            borderRadius: '14px',
            boxShadow: '0 16px 40px rgba(0,0,0,0.14)',
            padding: '10px',
          }}
          role="dialog"
          aria-modal="false"
          aria-label={t?.addCats || (locale === 'ru' ? 'Добавить категорию' : 'Санат қосу')}
        >
          <input
            ref={searchInputRef}
            className="gl-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t?.searchCat || (locale === 'ru' ? 'Поиск категории…' : 'Санатты іздеу…')}
          />

          <div
            className="gcie__adder-list"
            style={{
              marginTop: 8,
              maxHeight: '260px',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            {filteredToAdd.length === 0 ? (
              <div className="gcie__empty">
                {t?.addCatsEmpty || (locale === 'ru' ? 'Нет доступных категорий' : 'Қосатын санат жоқ')}
              </div>
            ) : (
              filteredToAdd.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="gcie__adder-item"
                  onClick={() => addCategory(c.id)}
                  disabled={savingAdd}
                >
                  <span>{byCatName(c, locale)}</span>
                  <span className="gcie__adder-plus">+</span>
                </button>
              ))
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 8,
              gap: 8,
            }}
          >
            <button
              type="button"
              className="gcie__adder-close"
              onClick={closeAdder}
            >
              {locale === 'ru' ? 'Закрыть' : 'Жабу'}
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="gcie">
      {!hideToolbar && (
        <div className="gcie__toolbar">
          {/* Кнопка редактирования управляется родителем, здесь скрыта как у тебя */}
          {/* {canEdit && (
            <button
              type="button"
              className={`gcie__editbtn ${editMode ? 'is-active' : ''}`}
              onClick={() => {
                setEditMode((v) => {
                  const next = !v;
                  if (!next) {
                    setShowAdder(false);
                    setQ('');
                  }
                  return next;
                });
              }}
              title={locale === 'ru' ? 'Изменить категории' : 'Санаттарды өзгерту'}
            >
              ✏️ {locale === 'ru' ? 'Изменить' : 'Өзгерту'}
            </button>
          )} */}

          {editMode && canAdd && (
            <button
              ref={toolbarAddBtnRef}
              type="button"
              className="gcie__plusbtn"
              onClick={() => toggleAdder('toolbar')}
              title={t?.addCats || (locale === 'ru' ? 'Добавить категорию' : 'Санат қосу')}
              aria-expanded={showAdder}
              aria-haspopup="dialog"
            >
              ＋ {t?.addCats || (locale === 'ru' ? 'Добавить' : 'Қосу')}
            </button>
          )}

          {editMode && isPreSchool && (
            <span className="gcie__hint">
              {t?.addCatsDisabledPreSchool ||
                (locale === 'ru'
                  ? 'Для дошкольного уровня категория фиксированная'
                  : 'Мектепке дейінгі деңгей үшін санат бекітілген')}
            </span>
          )}
        </div>
      )}

      {/* chips */}
      <div className="gcie__chips">
        {cats.map((cat, idx) => {
          const filled = !!cat.is_filled;
          const removable = editMode && !filled && canEdit;
          const busy = !!busyIds[cat.id];

          return (
            <div
              key={cat.id}
              className={[
                'gcie-chip-wrap',
                editMode ? 'is-editing' : '',
                removable ? 'is-removable' : '',
              ].join(' ')}
              style={{ animationDelay: `${idx * 25}ms` }}
            >
              <button
                type="button"
                disabled={filled || busy}
                className={[
                  'gl-chip',
                  'gl-chip--btn',
                  filled ? 'gl-chip--ok gl-chip--disabled' : 'gl-chip--todo',
                  editMode ? 'gcie-chip--jiggle' : '',
                ].join(' ')}
                onClick={() => handleChipClick(cat)}
                title={
                  filled
                    ? locale === 'ru'
                      ? 'Уже заполнено'
                      : 'Толтырылған'
                    : editMode
                    ? (locale === 'ru' ? 'Режим редактирования' : 'Өңдеу режимі')
                    : (locale === 'ru' ? 'Нужно заполнить' : 'Толтыру керек')
                }
              >
                {byCatName(cat, locale)}
              </button>

              {removable && (
                <button
                  type="button"
                  className="gcie-chip__remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeCategory(cat);
                  }}
                  disabled={busy}
                  aria-label={locale === 'ru' ? 'Удалить категорию' : 'Санатты жою'}
                  title={locale === 'ru' ? 'Удалить' : 'Жою'}
                >
                  {busy ? '…' : '×'}
                </button>
              )}
            </div>
          );
        })}

        {/* inline plus bubble (iphone-like) */}
        {editMode && canAdd && (
          <button
            ref={hideToolbar ? chipAddBtnRef : null}
            type="button"
            className="gcie-chip-add"
            onClick={() => toggleAdder('chip')}
            title={t?.addCats || (locale === 'ru' ? 'Добавить категорию' : 'Санат қосу')}
            aria-expanded={showAdder}
            aria-haspopup="dialog"
          >
            +
          </button>
        )}
      </div>

      {/* Popup rendered in body */}
      {adderPopup}
    </div>
  );
}